import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { SportPratique } from "@/types/intentProfile";

export type UserIntent =
  | "quick_session"
  | "structured_program"
  | "understand_training"
  | "track_progress"
  | "explore_platform"
  // Goal-based simple presets
  | "create_session"
  | "follow_program"
  | "learn"
  | "explore";

/** Simple-mode goal presets */
export type SimpleGoal = "create_session" | "follow_program" | "learn" | "track_progress" | "explore";

export interface UserIntentProfileData {
  objectif_principal: string;
  objectifs_secondaires: string[] | null;
  intentions_transversales: string[] | null;
  objectif_libre: string | null;
  sports_pratiques: SportPratique[] | null;
  niveau_sportif: string;
  age: number;
  sexe: string | null;
  poids_kg: number | null;
  taille_cm: number | null;
  jours_par_semaine: number;
  duree_par_session: number;
  blessures_connues: Array<{ zone: string; gravite: string; encore_active: boolean }> | null;
  materiel_disponible: string | null;
  lieu: string | null;
  restrictions_medicales: string | null;
  exercices_aimes: string[] | null;
  exercices_evites: string[] | null;
  priorite_actuelle: string;
  completed: boolean;
  profile_version: number;
  updated_at: string;
  user_intent: UserIntent | null;
  /** Primary goal for simple-mode preset */
  primary_goal: SimpleGoal | null;
  /** Optional secondary goal for suggestions */
  secondary_goal: SimpleGoal | null;
}

/** Stable query key used by all consumers and invalidations */
export const USER_INTENT_PROFILE_KEY = ["user-intent-profile"] as const;

export function useUserIntentProfile() {
  const { user } = useAuth();

  const { data: profile = null, isLoading: loading } = useQuery({
    queryKey: [...USER_INTENT_PROFILE_KEY, user?.id],
    queryFn: async (): Promise<UserIntentProfileData | null> => {
      if (!user) return null;

      // Parallel: DB row + sensitive data from edge function
      const dbPromise = supabase
        .from("user_intent_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      const sessionRes = await supabase.auth.getSession();
      const token = sessionRes.data?.session?.access_token;

      const sensitivePromise = token
        ? Promise.race([
            fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-sensitive-data?type=intent_sensitive`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
                },
              },
            ).then(async (res) => (res.ok ? res.json() : null)),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 8000)),
          ]).catch(() => null)
        : Promise.resolve(null);

      const [dbRes, sensitive] = await Promise.all([dbPromise, sensitivePromise]);
      const data = dbRes.data;
      if (!data) return null;

      const rawIntent = (data as any).user_intent as UserIntent | null;
      const storedPrimary = (data as any).primary_goal as SimpleGoal | null;
      const storedSecondary = (data as any).secondary_goal as SimpleGoal | null;
      const derivedPrimary = storedPrimary ?? mapIntentToGoal(rawIntent);

      return {
        objectif_principal: data.objectif_principal,
        objectifs_secondaires: data.objectifs_secondaires,
        niveau_sportif: data.niveau_sportif,
        age: data.age,
        sexe: data.sexe,
        poids_kg: data.poids_kg,
        taille_cm: data.taille_cm,
        jours_par_semaine: data.jours_par_semaine,
        duree_par_session: data.duree_par_session,
        priorite_actuelle: data.priorite_actuelle,
        completed: data.completed,
        profile_version: (data as any).profile_version || 1,
        updated_at: (data as any).updated_at || "",
        intentions_transversales: sensitive?.intentions_transversales ?? (data as any).intentions_transversales ?? [],
        objectif_libre: sensitive?.objectif_libre ?? (data as any).objectif_libre ?? null,
        sports_pratiques: sensitive?.sports_pratiques ?? (Array.isArray((data as any).sports_pratiques) ? (data as any).sports_pratiques : []),
        blessures_connues: sensitive?.blessures_connues ?? (data.blessures_connues as UserIntentProfileData["blessures_connues"]),
        materiel_disponible: sensitive?.materiel_disponible ?? data.materiel_disponible,
        lieu: sensitive?.lieu ?? data.lieu,
        restrictions_medicales: sensitive?.restrictions_medicales ?? data.restrictions_medicales,
        exercices_aimes: sensitive?.exercices_aimes ?? data.exercices_aimes,
        exercices_evites: sensitive?.exercices_evites ?? data.exercices_evites,
        user_intent: rawIntent,
        primary_goal: derivedPrimary,
        secondary_goal: storedSecondary,
      };
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  return { profile, loading };
}

/** Map legacy user_intent to SimpleGoal */
function mapIntentToGoal(intent: UserIntent | null): SimpleGoal | null {
  if (!intent) return null;
  const map: Record<string, SimpleGoal> = {
    quick_session: "create_session",
    structured_program: "follow_program",
    understand_training: "learn",
    track_progress: "track_progress",
    explore_platform: "explore",
    create_session: "create_session",
    follow_program: "follow_program",
    learn: "learn",
  };
  return map[intent] ?? null;
}
